import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../helpers/api";
import { styled } from "@mui/material/styles";
import { Box, Typography, CircularProgress } from "@mui/material";
import AppTheme from "../shared-theme/AppTheme";
import MuiCard from "@mui/material/Card";
import CssBaseline from "@mui/material/CssBaseline";
import Stack from "@mui/material/Stack";

const Card = styled(MuiCard)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  alignSelf: "center",
  width: "100%",
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: "auto",
  boxShadow:
    "hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px",
  [theme.breakpoints.up("sm")]: {
    width: "450px",
  },
  ...theme.applyStyles("dark", {
    boxShadow:
      "hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px",
  }),
}));

const GradientBackground = styled("div")({
  position: "fixed",
  inset: 0,
  background: "radial-gradient(circle at top left, #041C2C 0%, #22143C 100%)",
  zIndex: -1,
});

const SettingsContainer = styled(Stack)(({ theme }) => ({
  minHeight: "100dvh",
  padding: theme.spacing(2),
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.up("sm")]: {
    padding: theme.spacing(4),
  },
}));

export default function DisableTracking(props: {
  disableCustomTheme?: boolean;
}) {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<"loading" | "success" | "error">(
    "loading"
  );
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setStatus("loading");
      if (!token) {
        setStatus("error");
        return;
      }

      await api
        .get(`/disable?token=${token}`)
        .then((res) => {
          setStatus("success");
          setUrl(res.data.url);
        })
        .catch((error: any) => {
          setStatus("error");
        });
    })();
  }, [token]);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <GradientBackground />
      <SettingsContainer direction="column" justifyContent="space-between">
        <Card variant="outlined" sx={{ textAlign: "center" }}>
          {status === "loading" && (
            <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
              <CircularProgress />
            </Box>
          )}

          {status === "success" && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Tracking disabled</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {`Tracking has been disabled for`}
              </Typography>
              <Typography variant="body1" sx={{ mb: 3 }}>
                <a
                  href={url || undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {url}
                </a>
              </Typography>
            </>
          )}
          {status === "error" && !token && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Missing Token</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {"No disable token found"}
              </Typography>
            </>
          )}
          {status === "error" && token && (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 1,
                  mb: 2,
                }}
              >
                <Typography variant="h6">Error disabling tracking</Typography>
              </Box>
              <Typography variant="body1" sx={{ mb: 3 }}>
                {
                  "There was an error disabling tracking. Please try again later."
                }
              </Typography>
            </>
          )}
        </Card>
      </SettingsContainer>
    </AppTheme>
  );
}
